import type { ColumnMapping, TargetField } from './types';

/**
 * Synonyms per target field. Matched against normalised headers (lowercased,
 * non-alphanumerics stripped) — so "Account Code", "account_code" and
 * "AccountCode" all collapse to "accountcode". Order within each list is
 * preference order (earlier = stronger).
 */
const SYNONYMS: Record<TargetField, string[]> = {
  name: ['name', 'clientname', 'company', 'companyname', 'customername', 'suppliername', 'tradingname', 'client', 'businessname'],
  code: ['code', 'accountcode', 'account', 'customercode', 'suppliercode', 'clientcode', 'ref', 'reference'],
  billingName: ['billingname', 'invoicename', 'billto', 'invoiceto'],
  taxNumber: ['taxnumber', 'vatnumber', 'vat', 'taxno', 'vatno', 'taxregistration'],
  tags: ['tags', 'servicetags', 'labels'],
  addrName: ['locationname', 'sitename', 'site', 'addressname', 'depot', 'branch'],
  fullAddress: ['address', 'fulladdress', 'addressline', 'addressline1', 'address1', 'street', 'addr'],
  city: ['city', 'town', 'locality'],
  postcode: ['postcode', 'postalcode', 'zip', 'zipcode', 'eircode', 'postal'],
  country: ['country', 'countrycode', 'countryisocode', 'countryiso', 'iso'],
  latitude: ['latitude', 'lat'],
  longitude: ['longitude', 'lng', 'long', 'lon'],
};

/** Target fields in match-priority order — more specific / required first, so
 *  a header that could match several (e.g. "name" vs "billingname") is claimed
 *  by the strongest candidate before the looser ones get a turn. */
const MATCH_ORDER: TargetField[] = [
  'latitude',
  'longitude',
  'postcode',
  'country',
  'city',
  'billingName',
  'taxNumber',
  'tags',
  'addrName',
  'fullAddress',
  'code',
  'name',
];

const normalise = (h: string) => h.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Best-effort auto-map of source headers → target fields. Each header is
 * claimed by at most one target; each target takes at most one header. Tries
 * exact synonym match first, then startsWith, then contains. Unmatched targets
 * are simply left out of the result (operator maps them manually).
 */
export function autoMatchColumns(headers: string[]): ColumnMapping {
  const normalised = headers.map((h) => ({ raw: h, norm: normalise(h) }));
  const usedHeaders = new Set<string>();
  const mapping: ColumnMapping = {};

  const claim = (target: TargetField, raw: string) => {
    mapping[target] = raw;
    usedHeaders.add(raw);
  };

  for (const target of MATCH_ORDER) {
    const syns = SYNONYMS[target];
    let best: string | null = null;

    // Pass 1: exact equality.
    for (const { raw, norm } of normalised) {
      if (usedHeaders.has(raw)) continue;
      if (syns.includes(norm)) {
        best = raw;
        break;
      }
    }
    // Pass 2: startsWith / endsWith.
    if (!best) {
      for (const { raw, norm } of normalised) {
        if (usedHeaders.has(raw)) continue;
        if (syns.some((s) => norm.startsWith(s) || norm.endsWith(s))) {
          best = raw;
          break;
        }
      }
    }
    // Pass 3: contains.
    if (!best) {
      for (const { raw, norm } of normalised) {
        if (usedHeaders.has(raw)) continue;
        if (syns.some((s) => norm.includes(s))) {
          best = raw;
          break;
        }
      }
    }

    if (best) claim(target, best);
  }

  return mapping;
}
